/*
 * duo-fidget-recommender.js  —  Duo Gift Arcade discovery core (Fidget Match-3)
 * ---------------------------------------------------------------------------
 * PURE LOGIC. No DOM, no network, no Date.now(), no Math.random(). The Fidget
 * Match-3 reuses the gem-agnostic board engine (duo-arcade-engine.js); this
 * module owns the two fidget-specific pieces:
 *
 *   1. pickBoard()      deterministic board pool: 6 gems, each from a DIFFERENT
 *                       interest cluster, so which you clear most reveals a real
 *                       preference (not board luck across one theme). Seeded, so
 *                       the pool is reproducible for server replay.
 *   2. recommendGifts() turns the per-fidget clear tally into a transparent gift
 *                       recommendation: the cluster you cleared most -> the
 *                       fidget you engaged with + cluster siblings (pulled from
 *                       the WHOLE catalog, including ones not on your board) +
 *                       the matching set as an upsell, with an honest "why" line.
 *
 * Runs in the browser (window.DuoFidgetRecommender) and Node (module.exports),
 * one source of truth for the storefront, the unit tests, and any future replay.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.DuoFidgetRecommender = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  // Small self-contained PRNG so this module needs no engine dependency in Node.
  // Matches the engine's mulberry32 so a seed maps to the same stream everywhere.
  function makeRng(seed) {
    var a = seed >>> 0;
    return function () {
      a |= 0; a = (a + 0x6d2b79f5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function hashSeed(value) {
    var str = String(value);
    var h = 2166136261 >>> 0;
    for (var i = 0; i < str.length; i += 1) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
    return h >>> 0;
  }
  // Deterministic Fisher-Yates using the supplied rng (mutates + returns arr).
  function shuffle(arr, rng) {
    for (var i = arr.length - 1; i > 0; i -= 1) {
      var j = Math.floor(rng() * (i + 1));
      var t = arr[i]; arr[i] = arr[j]; arr[j] = t;
    }
    return arr;
  }

  var DEFAULT_LABELS = {
    foodie: 'Foodie', drinks: 'Cocktail Lover', cats: 'Cat Lover', dogs: 'Dog Lover',
    'bears-parents': 'Parent', plants: 'Plant Lover', 'spooky-funny': 'Spooky',
    'true-crime': 'True Crime', outdoors: 'Outdoorsy', whimsy: 'Feel-Good'
  };

  // Accept either the catalog module ({fidgets, clusterLabels}) or a bare array.
  function readCatalog(catalog) {
    if (catalog && catalog.fidgets) return { fidgets: catalog.fidgets, labels: catalog.clusterLabels || DEFAULT_LABELS };
    return { fidgets: catalog || [], labels: DEFAULT_LABELS };
  }

  /* ----------------------------------------------------------------- *
   * pickBoard: 6 gems from distinct clusters, deterministic from seed
   * ----------------------------------------------------------------- */
  function pickBoard(opts) {
    opts = opts || {};
    var c = readCatalog(opts.catalog);
    var n = opts.n || 6;
    var rng = makeRng(hashSeed(opts.seed != null ? opts.seed : 'duo-fidget'));

    var gems = c.fidgets.filter(function (f) { return f.gem && f.available !== false; });
    var byCluster = {};
    gems.forEach(function (f) { (byCluster[f.cluster] = byCluster[f.cluster] || []).push(f); });

    var clusters = Object.keys(byCluster);
    shuffle(clusters, rng);

    var board = [];
    for (var i = 0; i < clusters.length && board.length < n; i += 1) {
      var pool = byCluster[clusters[i]].slice();
      shuffle(pool, rng);
      board.push(pool[0]);
    }
    // Fewer clusters than n: fill with extra distinct gems.
    if (board.length < n) {
      var extra = gems.filter(function (f) { return board.indexOf(f) < 0; });
      shuffle(extra, rng);
      while (board.length < n && extra.length) board.push(extra.shift());
    }
    shuffle(board, rng);
    return board;
  }

  /* ----------------------------------------------------------------- *
   * recommendGifts: clear tally -> transparent gift recommendation
   * ----------------------------------------------------------------- */
  function buildReason(shortNames, clusterLabel) {
    if (!shortNames.length) return 'Here are a few of our most-loved fidgets to start with.';
    var list;
    if (shortNames.length === 1) list = 'the ' + shortNames[0];
    else if (shortNames.length === 2) list = 'the ' + shortNames[0] + ' and the ' + shortNames[1];
    else list = 'the ' + shortNames[0] + ', the ' + shortNames[1] + ', and more';
    return 'You kept snapping ' + list + ', so here are our ' + clusterLabel + ' picks.';
  }

  function recommendGifts(opts) {
    opts = opts || {};
    var c = readCatalog(opts.catalog);
    var tally = opts.tally || {};
    var maxPicks = opts.maxPicks || 3;

    var byId = {};
    c.fidgets.forEach(function (f) { byId[f.id] = f; });

    // cleared gems, most-cleared first (stable tiebreak by id)
    var cleared = Object.keys(tally)
      .filter(function (id) { return tally[id] > 0 && byId[id]; })
      .map(function (id) { return { f: byId[id], clears: tally[id] }; })
      .sort(function (a, b) { return (b.clears - a.clears) || (a.f.id < b.f.id ? -1 : 1); });

    // cluster totals
    var clusterScore = {};
    cleared.forEach(function (x) { clusterScore[x.f.cluster] = (clusterScore[x.f.cluster] || 0) + x.clears; });

    // topCluster: most clears; tiebreak = cluster of the single most-cleared
    // fidget; then name. Fallback to the biggest catalog cluster if nothing cleared.
    var topCluster;
    var keys = Object.keys(clusterScore);
    if (!keys.length) {
      var sizes = {};
      c.fidgets.forEach(function (f) { if (f.gem) sizes[f.cluster] = (sizes[f.cluster] || 0) + 1; });
      topCluster = Object.keys(sizes).sort(function (a, b) { return sizes[b] - sizes[a]; })[0] || 'foodie';
    } else {
      topCluster = keys.sort(function (a, b) {
        if (clusterScore[b] !== clusterScore[a]) return clusterScore[b] - clusterScore[a];
        var ra = cleared.findIndex(function (x) { return x.f.cluster === a; });
        var rb = cleared.findIndex(function (x) { return x.f.cluster === b; });
        if (ra < 0) ra = 1e9; if (rb < 0) rb = 1e9;
        if (ra !== rb) return ra - rb;
        return a < b ? -1 : 1;
      })[0];
    }

    // picks: lead with the fidget they actually engaged with in topCluster, then
    // cluster siblings from the whole catalog (the discovery: items not on board),
    // then fill from runner-up clusters, then any gem. Never a set, never a dup.
    var picks = [], seen = {};
    function add(f) { if (f && f.gem && !seen[f.id]) { seen[f.id] = true; picks.push(f); } }
    cleared.forEach(function (x) { if (x.f.cluster === topCluster && picks.length < maxPicks) add(x.f); });
    c.fidgets.forEach(function (f) {
      if (f.cluster === topCluster && f.available !== false && picks.length < maxPicks) add(f);
    });
    if (picks.length < maxPicks) {
      Object.keys(clusterScore).filter(function (k) { return k !== topCluster; })
        .sort(function (a, b) { return clusterScore[b] - clusterScore[a]; })
        .forEach(function (cl) {
          c.fidgets.forEach(function (f) { if (f.cluster === cl && f.available !== false && picks.length < maxPicks) add(f); });
        });
    }
    if (picks.length < maxPicks) {
      c.fidgets.forEach(function (f) { if (f.available !== false && picks.length < maxPicks) add(f); });
    }

    // set upsell: a set whose setCovers includes topCluster; most specific wins
    // (fewest covers), so plants -> Botanical Set before the variety set.
    var sets = c.fidgets.filter(function (f) {
      return f.isSet && f.available !== false && (f.setCovers || []).indexOf(topCluster) >= 0;
    }).sort(function (a, b) { return (a.setCovers || []).length - (b.setCovers || []).length; });
    var setUpsell = sets.length ? sets[0] : null;

    var clusterLabel = c.labels[topCluster] || topCluster;
    var inCluster = cleared.filter(function (x) { return x.f.cluster === topCluster; })
      .map(function (x) { return x.f.short; });

    return {
      topCluster: topCluster,
      clusterLabel: clusterLabel,
      picks: picks,
      setUpsell: setUpsell,
      reason: buildReason(inCluster, clusterLabel),
      breakdown: clusterScore,
      lead: cleared.length ? cleared[0].f : null
    };
  }

  return {
    makeRng: makeRng,
    hashSeed: hashSeed,
    pickBoard: pickBoard,
    recommendGifts: recommendGifts,
    CLUSTER_LABELS: DEFAULT_LABELS
  };
});
